import seedCourses from '@/data/seed_courses.json';

export interface SeedCourse {
  chapter_count: number | null;
  description: string;
  features: string[];
  id: string;
  image_url: string;
  name: string;
  subject: string;
}

export const COURSE_SUBJECT_OPTIONS = [
  'Astronomy',
  'Biology',
  'Chemistry',
  'Economics',
  'History',
  'Mathematics',
  'Physics',
  'Political Science',
  'Psychology',
  'Other',
] as const;

function normalizeString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function normalizeSeedCourse(value: unknown): SeedCourse | null {
  if (typeof value !== 'object' || value === null) {
    return null;
  }

  const record = value as Record<string, unknown>;
  const id = normalizeString(record.course_id);
  const name = normalizeString(record.title);
  const description = normalizeString(record.description);
  const subject = normalizeString(record.parent_subject);

  if (!id || !name || !description || !subject) {
    return null;
  }

  const features = Array.isArray(record.bullets)
    ? record.bullets
        .map((entry) => normalizeString(entry))
        .filter((entry): entry is string => Boolean(entry))
    : [];

  let chapterCount: number | null = null;

  for (const feature of features) {
    const match = feature.match(/Number of chapters:\s*(\d+)/i);

    if (match) {
      chapterCount = Number.parseInt(match[1], 10);
      break;
    }
  }

  return {
    chapter_count: chapterCount,
    description,
    features,
    id,
    image_url: normalizeString(record.image) ?? '',
    name,
    subject,
  };
}

export const ALL_SEED_COURSES = (Array.isArray(seedCourses) ? seedCourses : [])
  .map((entry) => normalizeSeedCourse(entry))
  .filter((entry): entry is SeedCourse => entry !== null);

export function filterSeedCoursesBySubject(
  courses: SeedCourse[],
  subject: string | null | undefined,
) {
  const normalizedSubject = normalizeString(subject);

  if (!normalizedSubject) {
    return courses;
  }

  return courses.filter((course) => course.subject === normalizedSubject);
}

export function useSeedCourses(subject: string | null | undefined) {
  return filterSeedCoursesBySubject(ALL_SEED_COURSES, subject);
}
